import { Component, inject, input, output, signal, OnDestroy, AfterViewInit, ViewChild, ElementRef, effect } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { StripePaymentService } from '../../services/stripe-payment.service';
import type { Stripe, StripeElements, StripePaymentElement } from '@stripe/stripe-js';

/**
 * Google Fonts configuration for Stripe secure iframe.
 */
const STRIPE_FONTS = [
  { cssSrc: 'https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap' },
];

/**
 * Stripe Payment Element appearance configuration.
 * Uses Stripe's Appearance API to match the exact app typography and color system:
 * - Font: "Inter", sans-serif (matching app typography)
 * - Background: #1a1a1f (modal dark background)
 * - Accent/Primary: #e84057 (neon pink)
 * - Muted Labels/Text: #6c757d (secondary grey)
 * - Borders: #2d2d30 / #6c757d
 */
const STRIPE_APPEARANCE = {
  theme: 'night' as const,
  variables: {
    colorPrimary: '#e84057',
    colorBackground: '#1a1a1f',
    colorText: '#ffffff',
    colorDanger: '#e84057',
    fontFamily: '"Inter", system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    borderRadius: '0.375rem',
    colorTextSecondary: '#6c757d',
    colorTextPlaceholder: '#6c757d',
  },
  rules: {
    '.Input': {
      backgroundColor: 'rgba(0, 0, 0, 0.25)',
      border: '1px solid #6c757d',
      boxShadow: 'none',
      fontFamily: '"Inter", system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    },
    '.Input:focus': {
      borderColor: '#e84057',
      boxShadow: '0 0 10px rgba(232, 64, 87, 0.2)',
    },
    '.Label': {
      color: '#6c757d',
      fontSize: '0.72rem',
      fontWeight: '700',
      textTransform: 'uppercase' as const,
      fontFamily: '"Inter", system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    },
    '.Tab': {
      backgroundColor: 'rgba(0, 0, 0, 0.2)',
      border: '1px solid #2d2d30',
      color: '#888',
      fontFamily: '"Inter", system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    },
    '.Tab:hover': {
      color: '#fff',
      backgroundColor: 'rgba(255, 255, 255, 0.03)',
      borderColor: '#444',
    },
    '.Tab--selected': {
      backgroundColor: 'rgba(232, 64, 87, 0.08)',
      borderColor: '#e84057',
      color: '#fff',
      boxShadow: '0 0 10px rgba(232, 64, 87, 0.15)',
    },
  },
};

@Component({
  selector: 'app-checkout-modal',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './checkout-modal.html',
  styleUrl: './checkout-modal.scss',
})
export class CheckoutModal implements AfterViewInit, OnDestroy {
  item = input.required<any>();
  close = output<void>();
  purchaseSuccess = output<any>();

  @ViewChild('stripeElement') stripeElementRef!: ElementRef<HTMLDivElement>;

  // Injected Stripe & PayPal payment service
  private stripeService = inject(StripePaymentService);

  // Payment Options: 'stripe' | 'paypal'
  paymentMethod = signal<'stripe' | 'paypal'>('stripe');

  // Subscription terms acceptance & form completion
  termsAccepted = signal(false);
  isFormComplete = signal(false);

  // Payment State Machine: 'idle' | 'loading_element' | 'ready' | 'processing' | 'success' | 'error'
  paymentState = signal<'idle' | 'loading_element' | 'ready' | 'processing' | 'success' | 'error'>('idle');
  processingMessage = signal('');
  errorMessage = signal('');

  // Stripe instances (only loaded when Stripe payment method is active)
  private stripe: Stripe | null = null;
  private elements: StripeElements | null = null;
  private paymentElement: StripePaymentElement | null = null;
  private clientSecret: string | null = null;
  private subscriptionId: string | null = null;

  // Track whether we've already initialized for the current payment method
  private stripeInitialized = false;

  constructor() {
    // React to payment method changes — initialize Stripe element when stripe is selected
    effect(() => {
      const method = this.paymentMethod();
      if (method === 'stripe' && !this.stripeInitialized) {
        // Delay to ensure the DOM container is available
        setTimeout(() => this.initializeStripeElement(), 0);
      }
    });
  }

  ngAfterViewInit() {
    // If stripe is the default method, initialize on view init
    if (this.paymentMethod() === 'stripe') {
      this.initializeStripeElement();
    }
  }

  ngOnDestroy() {
    this.destroyStripeElement();
  }

  selectMethod(method: 'stripe' | 'paypal') {
    if (method === 'paypal') return; // PayPal is temporarily disabled
    if (method === this.paymentMethod()) return;

    // Destroy existing Stripe element when switching away
    if (method !== 'stripe') {
      this.destroyStripeElement();
    }

    this.paymentMethod.set(method);
    this.paymentState.set('idle');
    this.errorMessage.set('');
    this.stripeInitialized = false;
  }

  /**
   * Lazily loads Stripe.js, fetches payment/subscription clientSecret (pi_...),
   * then mounts the Stripe Payment Element into our custom modal layout.
   */
  /**
   * Lazily loads Stripe.js and mounts the Stripe Payment Element using Deferred Intent Creation mode.
   * Enables immediate UI mounting without initial backend latency.
   */
  private async initializeStripeElement() {
    if (this.stripeInitialized) return;
    const container = this.stripeElementRef?.nativeElement;
    if (!container) return;

    this.stripeInitialized = true;
    this.paymentState.set('loading_element');
    this.processingMessage.set('Loading secure payment form...');

    try {
      // 1. Lazily load Stripe.js
      this.stripe = await this.stripeService.getStripe();
      if (!this.stripe) {
        throw new Error('Failed to load Stripe.js');
      }

      const currentItem = this.item();
      const isSubscription = currentItem.isSubscription;
      const amountInCents = Math.round(currentItem.price * 100);

      // 2. Initialize Stripe Elements with Deferred Intent Creation mode
      this.elements = this.stripe.elements({
        mode: isSubscription ? 'subscription' : 'payment',
        amount: amountInCents,
        currency: 'usd',
        appearance: STRIPE_APPEARANCE,
        fonts: STRIPE_FONTS,
      });

      // 3. Create and mount Payment Element into custom modal container
      this.paymentElement = this.elements.create('payment', {
        layout: 'tabs',
      });
      this.paymentElement.mount(container);

      // 4. Set ready state when payment element finishes rendering & track form completion
      this.paymentElement.on('ready', () => {
        this.paymentState.set('ready');
      });

      this.paymentElement.on('change', (event: any) => {
        this.isFormComplete.set(!!event.complete);
      });

    } catch (err: any) {
      console.error('[CheckoutModal] Stripe initialization error:', err);
      this.errorMessage.set(err?.message || 'Failed to load payment form. Please try again.');
      this.paymentState.set('error');
      this.stripeInitialized = false;
    }
  }

  private destroyStripeElement() {
    if (this.paymentElement) {
      this.paymentElement.destroy();
      this.paymentElement = null;
    }
    this.elements = null;
    this.clientSecret = null;
    this.stripeInitialized = false;
    this.isFormComplete.set(false);
  }

  async submitPayment() {
    const currentItem = this.item();

    if (this.paymentMethod() === 'stripe') {
      await this.submitStripePayment(currentItem);
    } else {
      this.submitPayPalPayment(currentItem);
    }
  }

  /**
   * Submits Payment Element inputs, fetches server clientSecret, and confirms payment inline.
   */
  private async submitStripePayment(currentItem: any) {
    if (!this.stripe || !this.elements) {
      this.errorMessage.set('Payment form not ready. Please wait or refresh.');
      this.paymentState.set('error');
      return;
    }

    this.paymentState.set('processing');
    this.processingMessage.set('Processing your payment...');

    try {
      // 1. Submit element inputs for client-side validation
      const { error: submitError } = await (this.elements as any).submit();
      if (submitError) {
        this.errorMessage.set(submitError.message || 'Payment validation failed.');
        this.paymentState.set('error');
        return;
      }

      // 2. Fetch server-authorized clientSecret
      let clientSecret = '';
      if (currentItem.isSubscription) {
        const tier = currentItem.planTier || (currentItem.price >= 50 ? 'VIP' : 'Supporter');
        const res = await this.stripeService.createSubscription(
          currentItem.priceId || currentItem.id,
          tier
        ).toPromise();
        clientSecret = res!.clientSecret;
        this.subscriptionId = res!.subscriptionId ?? null;
      } else {
        const res = await this.stripeService.createPaymentIntent(
          currentItem.id,
          currentItem.price,
          currentItem.priceId
        ).toPromise();
        clientSecret = res!.clientSecret!;
      }

      if (!clientSecret) {
        throw new Error('Failed to retrieve payment authorization secret from server.');
      }

      // 3. Confirm payment inline using stripe.confirmPayment
      const { error, paymentIntent } = await this.stripe.confirmPayment({
        elements: this.elements,
        clientSecret: clientSecret,
        confirmParams: {
          return_url: window.location.origin + '/home/store?payment=success',
        },
        redirect: 'if_required',
      });

      if (error) {
        this.errorMessage.set(error.message || 'Payment failed.');
        this.paymentState.set('error');
      } else {
        if (currentItem.isSubscription && this.subscriptionId) {
          await this.stripeService.confirmSubscription(this.subscriptionId).toPromise();
        } else if (!currentItem.isSubscription && paymentIntent?.id) {
          await this.stripeService.confirmNameplatePurchase(paymentIntent.id).toPromise();
        }
        this.paymentState.set('success');
        this.purchaseSuccess.emit(currentItem);
      }
    } catch (err: any) {
      console.error('[CheckoutModal] Stripe confirmation error:', err);
      this.errorMessage.set(err?.message || 'An unexpected error occurred.');
      this.paymentState.set('error');
    }
  }

  private submitPayPalPayment(currentItem: any) {
    this.paymentState.set('processing');

    if (currentItem.isSubscription) {
      this.processingMessage.set('Processing PayPal Subscription...');
      const tier = currentItem.planTier || (currentItem.price >= 50 ? 'VIP' : 'Supporter');

      this.stripeService.createPayPalSubscription(currentItem.id, tier).subscribe({
        next: () => this.handleSuccess(),
        error: (err) => {
          console.error('[CheckoutModal] PayPal Subscription error:', err);
          this.handleSuccess();
        }
      });
    } else {
      this.processingMessage.set('Processing PayPal Payment...');
      this.stripeService.createPayPalOrder(currentItem.id, currentItem.price).subscribe({
        next: (res) => {
          this.recordNameplateIfApplicable('PayPal', res.orderId || 'PAYPAL-CONFIRMED');
        },
        error: (err) => {
          console.error('[CheckoutModal] PayPal Order error:', err);
          this.recordNameplateIfApplicable('PayPal', 'PAYPAL-ORDER-ERR');
        }
      });
    }
  }

  retryPayment() {
    this.errorMessage.set('');
    this.stripeInitialized = false;
    this.destroyStripeElement();
    this.paymentState.set('idle');

    if (this.paymentMethod() === 'stripe') {
      setTimeout(() => this.initializeStripeElement(), 0);
    }
  }

  private recordNameplateIfApplicable(provider: string, paymentId: string) {
    const currentItem = this.item();
    let numericId = 101;
    if (typeof currentItem.id === 'number') {
      numericId = currentItem.id;
    } else if (typeof currentItem.id === 'string') {
      const match = currentItem.id.match(/\d+/);
      if (match) numericId = parseInt(match[0], 10);
    }

    if (currentItem.category === 'Cosmetics' && !currentItem.isSubscription) {
      this.stripeService.purchaseNameplate(numericId, provider, paymentId).subscribe({
        next: () => this.handleSuccess(),
        error: () => this.handleSuccess()
      });
    } else {
      this.handleSuccess();
    }
  }

  private handleSuccess() {
    this.paymentState.set('success');
    this.purchaseSuccess.emit(this.item());
  }

  cancelPayment() {
    this.close.emit();
  }
}
